import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Grid from '../Grid';
import GridItem from '../GridItem';

import styles, { mainCls } from './styles';
import createStyle from 'shared-utils/src/createStyle';
import makeClassName from 'shared-utils/src/makeClassName';
import CaretIcon from 'shared-vectors/icons/Caret';
import { Icon } from 'shared-vectors';

const useStyles = createStyle(styles);

/**
 * Custom component toggleSwitch done in css
 */
const AccordionItemUnstyled = ({
  children,
  header,
  trigger,
  disabled,
  startOpen,
  forceClose,
  onToggle,
  animated,
  hasTrigger,
  className: classNameProp,
}) => {

  const cls = useStyles();
  const bodyEl = useRef(null);

  const [opened, setOpened] = useState(startOpen);
  useEffect(() => {
    if (startOpen) {
      setOpened(startOpen);
      if(bodyEl.current) {
        setTimeout(() => setSize(bodyEl.current?.scrollHeight));
      }
    }
  },[startOpen]);
  useEffect(() => {
    if (forceClose) {
      setOpened(false);
      setBlockTransition(false);
      setTimeout(() => setSize(0));
    }
  },[forceClose]);  
  const [size, setSize] = useState(startOpen ? null : 0);
  const [blockTransition, setBlockTransition] = useState(startOpen);

  const toggleSize = (ev) => {
    if (disabled) return;
    setOpened(false);
    setBlockTransition(false);
    onToggle(!size, { event: ev });
    setTimeout(() => {
      const sizeState = size !== 0 || size === null ? 0 : bodyEl.current?.scrollHeight;
      setSize(sizeState);
    });
  };

  const handleTrEnd = () => {
    setOpened(bodyEl.current?.offsetHeight > 0)  
  };

  const bodyStyle = blockTransition
    ? { transition: 'none', maxHeight: size ? `${size}px` : null }
    : !opened
    ? { maxHeight: `${size}px` }
    : {};

  const clsMain = makeClassName([cls[`${mainCls}`], classNameProp]);
  const toParent = { disabled, opened };
  const defTrigger = (o, hasRotate) => (
    <GridItem behavior="fixed" push="left" className="c-txt--f3">
      <Icon name={CaretIcon} className={`accordion-item__icon is-anim ${hasRotate ? 'rotate--180' : ''}`} />
    </GridItem>
  );

  const triggerEl = hasTrigger
    ? trigger ? trigger(opened, size, toParent) : defTrigger(opened, size)
    : null;

  return (
    <div className={clsMain}>
      <Grid
        className={makeClassName([cls[`${mainCls}__head`], 'is-clickable'])}
        align="center"
        onClick={toggleSize}
      >
        <>{header(size, toParent, opened)}</>
        <>{triggerEl}</>
      </Grid>
      <div
        ref={bodyEl}
        className={makeClassName([cls[`${mainCls}__body`], animated && cls[`${mainCls}__body--anim`]])}
        style={bodyStyle}
        onTransitionEnd={handleTrEnd}
      >
        <div className={makeClassName([cls[`${mainCls}__children`]])}>{children}</div>
      </div>
    </div>
  );
};

AccordionItemUnstyled.propTypes = {
  /**
   * Component class
   */
  className: PropTypes.string,
  /**
   * Callback to handle header beahvior
   */
  header: PropTypes.func,
  /**
   * Callback to handle the onClick beahvior
   */
  trigger: PropTypes.func,

  onToggle: PropTypes.func,

  /**
   * Set if has to be already opened or not
   */
  startOpen: PropTypes.bool,

  /**
   * Disable accordion
   */
  disabled: PropTypes.bool,

  /**
   * Disable accordion
   */
  animated: PropTypes.bool,

  hasTrigger: PropTypes.bool,
};

AccordionItemUnstyled.defaultProps = {
  className: '',
  header: () => {},
  trigger: null,
  onToggle: () => {},
  startOpen: false,
  disabled: false,
  animated: false,
  hasTrigger: true,
};

export default AccordionItemUnstyled;
